import { Menu08Icon, Tick02Icon } from '@hugeicons-pro/core-solid-rounded';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { HugeIconsWrapper } from './HugeIconsWrapper';
import { Box } from '../gluestack/box';
import { HStack } from '../gluestack/hstack';
import { Text } from '../gluestack/text';

import { useFormatCount } from '@/hooks/useFormatCount';

interface ICountBadgeProps {
  count?: number;
  /** Full pill text. When set, rendered bold; mutually exclusive with `labelValue` / `labelCaption`. */
  label?: string;
  /** Bold segment (e.g. formatted count). Pair with `labelCaption` for mixed weight. */
  labelValue?: string;
  /** Normal-weight segment (e.g. translated suffix). */
  labelCaption?: string;
  /** Background class for the solid count pill(s). Defaults to primary. */
  solidBgClassName?: string;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  textClassName?: string;
  abbreviate?: boolean;
  isMyTypeFiltered?: boolean | null;
  /** When true, render nothing if `count` is 0 or less (uses `count` for split `labelValue` / `labelCaption` pills too). Ignored for My Type filter combo. */
  hideWhenZero?: boolean;
}

export const CountBadge: React.FC<ICountBadgeProps> = ({
  count = 0,
  label,
  labelValue,
  labelCaption,
  solidBgClassName = 'bg-primary-500',
  size = 'md',
  className = '',
  textClassName = '',
  abbreviate = false,
  isMyTypeFiltered,
  hideWhenZero = false,
}) => {
  const { t } = useTranslation();
  const { formatCount } = useFormatCount();

  const isFilterCombo = isMyTypeFiltered !== null && isMyTypeFiltered !== undefined;
  if (hideWhenZero && !isFilterCombo && count <= 0) {
    return null;
  }

  const hasSplitLabel = labelValue !== undefined || labelCaption !== undefined;
  if (hasSplitLabel) {
    const valueStr = labelValue ?? '';
    const capStr = labelCaption ?? '';
    if (valueStr === '' && capStr === '') return null;
    const sizeClasses =
      size === 'xs' ? 'px-2.5 py-1 min-w-0' : 'px-3 py-1.5 min-w-0';
    const textSize: 'xs' | 'sm' = size === 'xs' ? 'xs' : 'sm';
    return (
      <Box
        className={`items-center justify-center rounded-full ${solidBgClassName} ${sizeClasses} ${className}`}>
        <HStack className="items-center gap-1">
          {valueStr !== '' ? (
            <Text size={textSize} bold className={`text-white ${textClassName}`}>
              {valueStr}
            </Text>
          ) : null}
          {capStr !== '' ? (
            <Text size={textSize} className={`text-white ${textClassName}`}>
              {capStr}
            </Text>
          ) : null}
        </HStack>
      </Box>
    );
  }

  if (label !== undefined) {
    if (label === '') return null;
    const sizeClasses =
      size === 'xs' ? 'px-2.5 py-1 min-w-0' : 'px-3 py-1.5 min-w-0';
    const textSize: 'xs' | 'sm' = size === 'xs' ? 'xs' : 'sm';
    return (
      <Box
        className={`items-center justify-center rounded-full ${solidBgClassName} ${sizeClasses} ${className}`}>
        <Text size={textSize} bold className={`text-white ${textClassName}`}>
          {label}
        </Text>
      </Box>
    );
  }

  const shouldShowFilterPill = isFilterCombo;
  if (count <= 0 && !shouldShowFilterPill) return null;

  const sizeClasses = size === 'xs' ? 'px-1.5 py-1 min-w-7' : 'px-2 py-1.5 min-w-7';
  const textSize: 'xs' | 'sm' = size === 'xs' ? 'xs' : 'sm';
  const filterTextSize: 'xs' = 'xs';
  const iconSize = size === 'xs' ? 14 : 16;
  const displayCount = abbreviate ? formatCount(count) : Number(count).toLocaleString();
  const filterLabelAll = shouldShowFilterPill ? t('common.filters.all') : null;
  const filterLabelMyType = shouldShowFilterPill ? t('common.filters.myType') : null;
  const tickSize = size === 'xs' ? 10 : 12;

  if (!shouldShowFilterPill) {
    return (
      <Box
        className={`items-center justify-center rounded-full ${solidBgClassName} ${sizeClasses} ${className}`}>
        <Text size={textSize} bold className={`text-white ${textClassName}`}>
          {displayCount}
        </Text>
      </Box>
    );
  }

  const filterPillContent = isMyTypeFiltered ? (
    <HStack className="items-center gap-1 px-1">
      <Text size={filterTextSize} className="uppercase text-primary-700">
        {filterLabelMyType}
      </Text>
      <HugeIconsWrapper icon={Tick02Icon} size={tickSize} className="text-primary-700" />
    </HStack>
  ) : (
    <Text size={filterTextSize} className="px-1 uppercase text-primary-700">
      {filterLabelAll}
    </Text>
  );

  return (
    <HStack className="items-stretch">
      {count > 0 ? (
        <>
          <Box
            className={`z-10 items-center justify-center rounded-full ${solidBgClassName} ${sizeClasses} ${className}`}>
            <Text size={textSize} bold className={`text-white ${textClassName}`}>
              {displayCount}
            </Text>
          </Box>
          <Box
            className={`-ml-4 justify-center rounded-l-none rounded-r-full bg-primary-300 ${sizeClasses} pl-5 pr-2`}>
            {filterPillContent}
          </Box>
        </>
      ) : (
        <>
          <Box
            className={`z-10 items-center justify-center rounded-full ${solidBgClassName} ${sizeClasses} ${className}`}>
            <HugeIconsWrapper icon={Menu08Icon} size={iconSize} className="text-white" />
          </Box>
          <Box
            className={`-ml-4 justify-center rounded-l-none rounded-r-full bg-primary-300 ${sizeClasses} pl-5 pr-2`}>
            {filterPillContent}
          </Box>
        </>
      )}
    </HStack>
  );
};
